import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Cranbrook = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.cranbrook:About Cranbrook Paragraph 1">
            Cranbrook, nestled in the southeastern landscapes of{' '}
            <LinkText href="/rental-listings/british-columbia">
              British Columbia (BC)
            </LinkText>
            , epitomizes the blend of nature and urban living found in cities
            and neighborhoods. Framed by the Rocky and Purcell Mountains, the
            city offers a diverse economy that spans healthcare, education, and
            tourism, while its College of the Rockies adds to its educational
            fabric. Outdoor enthusiasts thrive in this Rocky Mountain Trench
            setting, enjoying activities such as hiking, fishing, and skiing.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.cranbrook:About Cranbrook Paragraph 2">
            Cranbrook’s rich heritage is showcased in the Canadian Museum of
            Rail Travel, reflecting the city’s historical roots. Its community
            spirit, healthcare facilities like the East Kootenay Regional
            Hospital, and accessibility through the Canadian Rockies
            International Airport underscore its role as a regional hub. This BC
            city captures the essence of a balanced lifestyle in a picturesque
            setting within the heart of{' '}
            <LinkText href={BcNeighbourhoodsLink}>
              British Columbia’s cities and neighborhoods
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,000 - $2,000',
          bed2: '$1,200 - $2,400',
          bed3: '$2,100 - $3,300'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.cranbrook:Apartment Hunting in Cranbrook Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Cranbrook
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cranbrook:Apartments for rent Cranbrook Paragraph">
              <LinkText href={links.apartment}>
                Renting an apartment in Cranbrook
              </LinkText>{' '}
              typically appeals to individuals, couples, or small families
              looking for a practical and convenient living space. Apartments
              for rent in Cranbrook range from older, more affordable buildings
              to newer, upscale complexes, often located near the city’s
              amenities like shops, restaurants, and public services, making
              them a popular choice for those seeking a blend of comfort and
              convenience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cranbrook:Houses for rent Cranbrook Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Cranbrook
              </LinkText>{' '}
              provide a middle ground between apartments and detached houses.
              These are often part of smaller communities, offering a sense of
              neighborhood with a bit more privacy and space than an apartment.
              Townhouses are popular among small families, couples, or
              professionals who appreciate having their own entrance and, in
              many cases, a small yard or patio.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cranbrook:Townhouses for rent Cranbrook Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Cranbrook
              </LinkText>{' '}
              offer a combination of communal living and private space. These
              multi-floor units, often part of friendly communities, are
              preferred by small families, couples, or individuals seeking a
              balance between having their own home and the convenience of
              shared maintenance responsibilities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cranbrook:Basements for rent Cranbrook Paragraph">
              Basements for rent in Cranbrook offer an affordable alternative
              for renters, particularly appealing to singles, students, or young
              professionals. These units, part of larger homes, typically
              provide basic amenities and a more private living space at a lower
              cost, though they may have limitations in terms of natural light
              and size.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cranbrook:Condos for rent Cranbrook Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Cranbrook
              </LinkText>{' '}
              cater to those seeking a low-maintenance lifestyle with a sense of
              community. These units often come with modern amenities and are
              designed for easy living. Condos are a favored choice for young
              professionals, retirees, or small families who value the
              convenience of having communal facilities like gyms or party
              rooms.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cranbrook:Rooms for rent Cranbrook Paragraph">
              <LinkText href={links.room}>Renting a room in Cranbrook</LinkText>{' '}
              is the most budget-friendly option, commonly chosen by students,
              temporary workers, or individuals seeking short-term or flexible
              living arrangements. Rooms for rent in Cranbrook involve sharing
              facilities like kitchens and bathrooms with other occupants,
              fostering a communal living atmosphere.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cranbrook:Short term rentals Cranbrook Paragraph">
              Cranbrook offers a variety of employment opportunities and some
              educational institutions, which may appeal to certain individuals
              looking for short-term rental options. However, the popularity of{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText> can
              vary widely and might not be as high in Cranbrook as it is in
              larger urban centers. While short-term rentals generally see
              increased demand in many cities during summer months and at the
              beginning of school semesters, this trend isn’t universal and can
              depend on the specific characteristics of each location. For many
              renters, the flexibility of short-term rentals is a significant
              appeal, and this flexibility often plays a crucial role in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding if short-term rentals are the right choice for their
                needs
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cranbrook:Cheap rentals Cranbrook Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Cranbrook
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Cranbrook, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.cranbrook:Living in Cranbrook Paragraph">
            Living in Cranbrook, British Columbia, offers a unique blend of
            scenic beauty and small-town charm. Located in the southeastern part
            of the province, Cranbrook is surrounded by awe-inspiring
            landscapes, including towering mountains, pristine lakes, and lush
            forests. The community in Cranbrook is known for its friendly and
            welcoming atmosphere, providing a range of amenities for both
            residents and visitors. From outdoor adventures like hiking and
            skiing to cultural experiences at local museums and art galleries,
            Cranbrook offers a diverse range of activities for everyone to
            enjoy. With its clean and safe environment, Cranbrook provides a
            peaceful and serene living experience that is perfect for those
            seeking tranquility.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.cranbrook:Living in Cranbrook Paragraph 2">
            If you’re not sure that Cranbrook is the right city for you, check
            out some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.cranbrook:Popular Neighbourhoods Cranbrook Paragraph">
          Like any city, Cranbrook is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Kootenay Orchards, Mount Royal, Southview, Highlands, East
          Kootenay, Wycliffe, Baker Hill, Tanglewood, and Fernie Road. For more
          info about Cranbrook, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.cranbrook:Local Landmarks Cranbrook Paragraph">
          Cranbrook, BC, is a city that boasts a variety of remarkable landmarks
          that showcase the area’s natural beauty and rich history. One such
          landmark is the Cranbrook History Centre, which allows visitors to
          delve into the region’s past through captivating exhibits and
          artifacts. The Canadian Museum of Rail Travel is another noteworthy
          landmark, displaying a collection of beautifully restored vintage
          trains that transport visitors back in time. For outdoor enthusiasts,
          the Fisher Peak Summit Trail offers breathtaking panoramic views of
          the surrounding mountains and valleys. Additionally, the Elizabeth
          Lake Wildlife Sanctuary provides a serene retreat for birdwatchers and
          nature lovers alike. These landmarks contribute to the unique
          character and allure of Cranbrook.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Cranbrook
